import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  Edit3,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { Section } from "@/hooks/use-proposals";

interface ProposalSectionsSidebarProps {
  sections: Section[];
  activeSection?: string | null;
  pendingSections?: Set<string>;
  onSelectSection: (id: string) => void;
  onAddSection?: () => void;
  isAddingSection?: boolean;
  // Mejora "Editabilidad del índice": mover entre hermanos, renombrar
  // inline y eliminar. Opcionales para que el sidebar degrade si el
  // padre no los provee.
  onReorder?: (sectionId: string, direction: -1 | 1) => void;
  onRename?: (sectionId: string, title: string) => void;
  onDelete?: (section: Section) => void;
  /** Crea una subsección dentro de la sección indicada (solo raíces). */
  onAddSubsection?: (parentId: string) => void;
  // Rediseño UX: el índice puede colapsarse a una tira de iconos para
  // ganar ancho de redacción. El estado lo gestiona el padre (persistido).
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

type SectionStatus = "empty" | "draft" | "complete" | "generating" | "error";

const STATUS_ICON: Record<
  SectionStatus,
  { icon: typeof CheckCircle2; className: string; spin?: boolean }
> = {
  generating: { icon: Loader2, className: "text-purple-600", spin: true },
  error: { icon: AlertTriangle, className: "text-red-500" },
  complete: { icon: CheckCircle2, className: "text-green-500" },
  draft: { icon: Edit3, className: "text-blue-500" },
  empty: { icon: Circle, className: "text-gray-300" },
};

/**
 * Devuelve el estado visual de una sección.
 * La generación IA asíncrona manda: "generating" mientras se genera y "error"
 * si falló. Si no, se deriva del contenido: "complete" (>50 palabras), "draft"
 * (algo de texto) o "empty".
 */
function getSectionStatus(section: Section): SectionStatus {
  if (section.generation_status === "GENERANDO") return "generating";
  if (section.generation_status === "ERROR") return "error";
  const text = (section.content_plain || "").trim();
  if (!text) return "empty";
  const words = text.split(/\s+/).length;
  if (words > 50) return "complete";
  return "draft";
}

interface SectionRowProps {
  section: Section;
  index: string;
  isActive: boolean;
  hasPending: boolean;
  isSub?: boolean;
  onSelect: () => void;
  // Acciones de edición (opcionales).
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRename?: (title: string) => void;
  onDelete?: () => void;
  onAddSubsection?: () => void;
}

function SectionRow({
  section,
  index,
  isActive,
  hasPending,
  isSub,
  onSelect,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRename,
  onDelete,
  onAddSubsection,
}: SectionRowProps) {
  const status = getSectionStatus(section);
  const statusConfig = STATUS_ICON[status];
  const Icon = statusConfig.icon;

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(section.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraftTitle(section.title);
      // foco al entrar en modo edición
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [editing, section.title]);

  const commitRename = () => {
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== section.title) {
      onRename?.(trimmed);
    }
    setEditing(false);
  };

  const showActions = !!(onMoveUp || onMoveDown || onRename || onDelete || onAddSubsection);

  return (
    <div
      className={cn(
        "group/row relative flex items-start gap-2 rounded-lg transition-colors",
        isSub ? "p-2" : "p-3",
        isActive
          ? "bg-blue-100 border border-blue-300"
          : "bg-white border border-gray-200 hover:bg-gray-50"
      )}
    >
      <Icon
        className={cn(
          "flex-shrink-0 mt-0.5",
          isSub ? "w-3.5 h-3.5" : "w-4 h-4",
          statusConfig.className,
          statusConfig.spin && "animate-spin"
        )}
      />
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "w-full rounded border border-blue-300 px-1.5 py-0.5 font-medium focus:outline-none focus:ring-2 focus:ring-blue-400",
              isSub ? "text-xs" : "text-sm"
            )}
            aria-label="Renombrar sección"
          />
        ) : (
          <button
            type="button"
            onClick={onSelect}
            className="block w-full text-left"
          >
            <p
              title={`${index}. ${section.title}`}
              className={cn(
                "font-medium truncate",
                isSub ? "text-xs" : "text-sm",
                isActive ? "text-blue-800" : "text-gray-700"
              )}
            >
              {index}. {section.title}
            </p>
            <div className="flex items-center gap-2 mt-1">
              {status === "generating" ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Generando…</span>
                </span>
              ) : status === "error" ? (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-500"
                  title={section.generation_error || "La generación falló"}
                >
                  <AlertTriangle className="w-3 h-3" />
                  <span>Error de generación</span>
                </span>
              ) : (
                <>
                  {section.ai_generated && (
                    <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                      <Sparkles className="w-3 h-3" />
                      <span>IA</span>
                    </span>
                  )}
                  {hasPending && (
                    <span
                      className="inline-flex items-center text-xs text-amber-500"
                      title="Cambios sin guardar"
                    >
                      <Clock className="w-3 h-3" />
                    </span>
                  )}
                </>
              )}
            </div>
          </button>
        )}
      </div>

      {showActions && !editing && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100 transition-opacity">
          {onMoveUp && (
            <button
              type="button"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              title="Subir"
              aria-label="Subir sección"
              className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              title="Bajar"
              aria-label="Bajar sección"
              className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          )}
          {/* Solo en secciones raíz: el modelo admite 2 niveles, así que una
              subsección no puede tener hijas. */}
          {onAddSubsection && !isSub && (
            <button
              type="button"
              onClick={onAddSubsection}
              title="Añadir subsección"
              aria-label="Añadir subsección"
              className="p-1 text-gray-400 hover:text-blue-600"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          {onRename && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              title="Renombrar"
              aria-label="Renombrar sección"
              className="p-1 text-gray-400 hover:text-gray-700"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              title="Eliminar"
              aria-label="Eliminar sección"
              className="p-1 text-gray-400 hover:text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sidebar con el listado ordenado de secciones de una propuesta.
 *
 * Mejora "Subsecciones de primera clase": agrupa por jerarquía. Las
 * secciones raíz se muestran como antes, y bajo cada una se renderizan
 * sus subsecciones (depth=1) indentadas, con su propia numeración
 * (1.1, 1.2…). Cada rama es colapsable.
 *
 * Mejora "Editabilidad del índice": cada fila ofrece (al hover) mover
 * arriba/abajo entre hermanos, renombrar inline y eliminar.
 */
export function ProposalSectionsSidebar({
  sections,
  activeSection,
  pendingSections,
  onSelectSection,
  onAddSection,
  isAddingSection = false,
  onReorder,
  onRename,
  onDelete,
  onAddSubsection,
  collapsed = false,
  onToggleCollapse,
}: ProposalSectionsSidebarProps) {
  // Agrupación: raíces y children por parent.
  const sorted = [...sections].sort((a, b) => a.section_order - b.section_order);
  const roots = sorted.filter((s) => !s.parent_section_id);
  const childrenByParent: Record<string, Section[]> = {};
  for (const s of sorted) {
    if (s.parent_section_id) {
      (childrenByParent[s.parent_section_id] ||= []).push(s);
    }
  }

  const [branchCollapsed, setBranchCollapsed] = useState<
    Record<string, boolean>
  >({});
  const toggle = (id: string) =>
    setBranchCollapsed((c) => ({ ...c, [id]: !c[id] }));

  // Ancho del panel, redimensionable arrastrando el borde derecho. Se
  // persiste en localStorage para que se mantenga entre sesiones. Los
  // títulos largos (sobre todo subsecciones) ya no se quedan cortados:
  // basta con ensanchar el panel (y hay tooltip al pasar el ratón).
  const SIDEBAR_MIN = 200;
  const SIDEBAR_MAX = 560;
  const SIDEBAR_DEFAULT = 256;
  const asideRef = useRef<HTMLElement>(null);
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return SIDEBAR_DEFAULT;
    const saved = Number(window.localStorage.getItem("proposalSidebarWidth"));
    return Number.isFinite(saved) && saved > 0
      ? Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, saved))
      : SIDEBAR_DEFAULT;
  });

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = asideRef.current?.offsetWidth ?? SIDEBAR_DEFAULT;
    let latest = startWidth;
    const onMove = (ev: MouseEvent) => {
      latest = Math.min(
        SIDEBAR_MAX,
        Math.max(SIDEBAR_MIN, startWidth + (ev.clientX - startX))
      );
      setWidth(latest);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try {
        window.localStorage.setItem("proposalSidebarWidth", String(latest));
      } catch {
        /* localStorage no disponible: ignorar */
      }
    };
    // Evita seleccionar texto mientras se arrastra.
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  // Estado colapsado: tira estrecha con la numeración + icono de estado de
  // cada sección raíz (tooltip con el título completo). Click = seleccionar.
  if (collapsed) {
    return (
      <aside className="w-12 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col items-center py-3 gap-1 h-full overflow-y-auto">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md"
          title="Mostrar índice de secciones"
          aria-label="Mostrar índice"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
        <div className="w-6 h-px bg-gray-200 my-1" />
        {roots.map((s, i) => {
          const isActive = activeSection === s.id;
          const cfg = STATUS_ICON[getSectionStatus(s)];
          const Icon = cfg.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelectSection(s.id)}
              title={`${i + 1}. ${s.title}`}
              className={cn(
                "relative w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium",
                isActive
                  ? "bg-purple-100 text-purple-700"
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              <span>{i + 1}</span>
              <Icon
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 w-3 h-3",
                  cfg.className
                )}
              />
            </button>
          );
        })}
      </aside>
    );
  }

  return (
    <aside
      ref={asideRef}
      style={{ width }}
      className="relative flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700">Secciones</h2>
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Ocultar índice"
              aria-label="Ocultar índice"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
        </div>
        {onAddSection && (
          <button
            type="button"
            onClick={onAddSection}
            disabled={isAddingSection}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>{isAddingSection ? "Añadiendo..." : "Añadir sección"}</span>
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        {roots.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8 px-2">
            No hay secciones todavía
          </p>
        ) : (
          <ul className="space-y-1">
            {roots.map((section, index) => {
              const isActive = activeSection === section.id;
              const hasPending = !!pendingSections?.has(section.id);
              const kids = childrenByParent[section.id] ?? [];
              const hasKids = kids.length > 0;
              const isCollapsed = !!branchCollapsed[section.id];

              return (
                <li key={section.id}>
                  <div className="relative flex items-stretch gap-1">
                    {hasKids ? (
                      <button
                        type="button"
                        onClick={() => toggle(section.id)}
                        className="px-1 text-gray-400 hover:text-gray-700"
                        title={isCollapsed ? "Mostrar apartados" : "Ocultar apartados"}
                        aria-label={isCollapsed ? "Mostrar apartados" : "Ocultar apartados"}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                      </button>
                    ) : (
                      <span className="w-5" aria-hidden />
                    )}
                    <div className="flex-1">
                      <SectionRow
                        section={section}
                        index={String(index + 1)}
                        isActive={isActive}
                        hasPending={hasPending}
                        onSelect={() => onSelectSection(section.id)}
                        canMoveUp={index > 0}
                        canMoveDown={index < roots.length - 1}
                        onMoveUp={onReorder ? () => onReorder(section.id, -1) : undefined}
                        onMoveDown={onReorder ? () => onReorder(section.id, 1) : undefined}
                        onRename={onRename ? (t) => onRename(section.id, t) : undefined}
                        onDelete={onDelete ? () => onDelete(section) : undefined}
                        onAddSubsection={
                          onAddSubsection ? () => onAddSubsection(section.id) : undefined
                        }
                      />
                    </div>
                  </div>

                  {hasKids && !isCollapsed && (
                    <ul className="mt-1 ml-6 pl-2 border-l-2 border-purple-100 space-y-1">
                      {kids.map((sub, subIdx) => (
                        <li key={sub.id}>
                          <SectionRow
                            section={sub}
                            index={`${index + 1}.${subIdx + 1}`}
                            isActive={activeSection === sub.id}
                            hasPending={!!pendingSections?.has(sub.id)}
                            isSub
                            onSelect={() => onSelectSection(sub.id)}
                            canMoveUp={subIdx > 0}
                            canMoveDown={subIdx < kids.length - 1}
                            onMoveUp={onReorder ? () => onReorder(sub.id, -1) : undefined}
                            onMoveDown={onReorder ? () => onReorder(sub.id, 1) : undefined}
                            onRename={onRename ? (t) => onRename(sub.id, t) : undefined}
                            onDelete={onDelete ? () => onDelete(sub) : undefined}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Handle de redimensionado: arrastra para ensanchar/estrechar el
          panel de secciones. Doble clic restaura el ancho por defecto. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Redimensionar panel de secciones"
        title="Arrastra para ajustar el ancho · doble clic para restablecer"
        onMouseDown={startResize}
        onDoubleClick={() => {
          setWidth(SIDEBAR_DEFAULT);
          try {
            window.localStorage.setItem(
              "proposalSidebarWidth",
              String(SIDEBAR_DEFAULT)
            );
          } catch {
            /* ignorar */
          }
        }}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300/60 active:bg-blue-400/70 transition-colors"
      />
    </aside>
  );
}

export default ProposalSectionsSidebar;
